interface HeightTransitionOptions {
	enterMs?: number;
	leaveMs?: number;
}

type TransitionHook = (el: Element, done: () => void) => void;

// Releasing to the natural height keeps the element correct when its content
// re-wraps; holding the collapsed height stops a flash back to full height in
// the frame before Vue applies `display: none`.
const RELEASE_TO_NATURAL_HEIGHT: FillMode = "none";
const HOLD_COLLAPSED: FillMode = "forwards";

/**
 * Vue `<Transition>` hooks that open and close an element on height alone, for
 * use with `:css="false"`. The element must clip its own overflow and must not
 * carry padding of its own — with `border-box`, padding floors the collapsed
 * height.
 */
export function useHeightTransition(options: HeightTransitionOptions = {}) {
	const { enterMs = 180, leaveMs = 140 } = options;

	const prefersReducedMotion = () =>
		import.meta.client &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	const animate = (
		el: Element,
		from: string,
		to: string,
		duration: number,
		easing: string,
		fill: FillMode,
		done: () => void
	) => {
		const target = el as HTMLElement;
		target.getAnimations().forEach((animation) => animation.cancel());

		if (prefersReducedMotion()) {
			done();
			return;
		}

		try {
			const animation = target.animate([{ height: from }, { height: to }], {
				duration,
				easing,
				fill
			});
			animation.onfinish = done;
			animation.oncancel = done;
		} catch {
			done();
		}
	};

	const onEnter: TransitionHook = (el, done) =>
		animate(
			el,
			"0px",
			`${el.scrollHeight}px`,
			enterMs,
			"ease-out",
			RELEASE_TO_NATURAL_HEIGHT,
			done
		);

	const onLeave: TransitionHook = (el, done) =>
		animate(
			el,
			`${(el as HTMLElement).offsetHeight}px`,
			"0px",
			leaveMs,
			"ease-in",
			HOLD_COLLAPSED,
			done
		);

	return { onEnter, onLeave };
}
